"use client";

import * as React from "react";
import { Button, type ButtonSize } from "@/components/button/button/button";
import { Dialog, DialogContent } from "@/components/dialog/dialog/dialog";
import { renderRichTextWithMutedStrikethrough } from "@/lib/richText";
import { captureEvent } from "@/lib/analytics";
import type { Blocks } from "@/types/cms";
import { cn } from "@/lib/utils";
import styles from "./alreadyButton.module.css";

/** Shape we care about from the looked-up Content Card. */
interface DialogCard {
  title?: string | null;
  eyebrow?: string | null;
  content?: Blocks | null;
}

export interface AlreadyButtonProps {
  /** Link opened in a NEW tab when clicked. */
  href: string;
  /** Button label — the CMS button's `copy`. */
  children: React.ReactNode;
  /** Custom ID of the Content Card shown in the dialog. */
  cardCustomID?: string;
  /** Button variant, mapped by the caller from the CMS `type`. */
  variant?: "primary" | "default" | "ghost";
  /** Button size, mapped by the caller from the CMS `size`. */
  size?: ButtonSize;
  className?: string;
}

/**
 * The "already downloaded?" action.
 *
 * One click does two things: the browser opens `href` in a new tab (it's a
 * real anchor, so this is a direct user action and can't be popup-blocked),
 * and the original tab opens a dialog showing the `already-dialog` Content
 * Card. The click handler deliberately does NOT preventDefault — the
 * navigation and the dialog both need to happen.
 *
 * The card is fetched once, on first open, from `/api/content-card`, so a
 * page carrying this button doesn't pay for content most visitors never see.
 */
const AlreadyButton = ({
  href,
  children,
  cardCustomID = "already-dialog",
  variant = "ghost",
  size = "default",
  className,
}: AlreadyButtonProps) => {
  const [open, setOpen] = React.useState(false);
  const [card, setCard] = React.useState<DialogCard | null>(null);
  const requested = React.useRef(false);

  const handleClick = () => {
    // Let the anchor's default (new tab) proceed; just also open the dialog.
    setOpen(true);

    // Captured explicitly rather than via an autocapture href-match action:
    // the href is a custom scheme (autoscreenshot://), and autocapture's
    // handling of non-http hrefs isn't worth betting a metric on. This is the
    // returning-user signal — they already have the app, so they never appear
    // in the .dmg download numbers.
    captureEvent("already_button_clicked", { href });

    if (requested.current) return;
    requested.current = true;

    fetch(`/api/content-card?customID=${encodeURIComponent(cardCustomID)}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setCard(data?.card ?? null))
      .catch(() => {
        // The new tab already did the useful thing; a failed lookup just
        // means no dialog. Never trap the user behind an error state.
        setCard(null);
      });
  };

  return (
    <>
      {/* The wrapper — not the button — carries the line break, so the
          button keeps its intrinsic size and matches its siblings. */}
      <span className={styles.ownLine}>
        <Button asChild variant={variant} size={size} className={className}>
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClick}
          >
            {children}
          </a>
        </Button>
      </span>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="sm" title={card?.title ?? undefined}>
          <div className={styles.dialogBody}>
            {card?.eyebrow ? (
              <span className={`eyebrow ${styles.eyebrow}`}>
                {card.eyebrow}
              </span>
            ) : null}
            {card?.content ? (
              <div className={styles.content}>
                {renderRichTextWithMutedStrikethrough(card.content, {
                  mutedParagraphs: false,
                })}
              </div>
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

AlreadyButton.displayName = "AlreadyButton";

export { AlreadyButton };
